import React, { useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { useMsal } from "@azure/msal-react";
import { clearUser } from "../../store/authSlice";
import { fetchUserPreferences } from "../../store/agentSlice";
import Sidebar from "../../components/Sidebar";
import PageLoader from "../../components/PageLoader";
import { Alert, CircularProgress, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "../Dashboard.scss";
import styles from "./performance.module.scss";

// Split text into max 2 lines by words (no truncation/ellipsis)
function splitIntoTwoLines(
  text: string,
  maxCharsPerLine: number = 70,
): [string, string | null] {
  if (!text) return ["", null];
  const words = text.trim().split(/\s+/);
  if (words.length === 0) return ["", null];
  let line1 = "";
  let line2 = "";
  for (const word of words) {
    const next1 = line1 ? `${line1} ${word}` : word;
    if (!line2 && next1.length <= maxCharsPerLine) {
      line1 = next1;
    } else if (!line2) {
      if (line1) line2 = word;
      else line1 = word;
    } else {
      const next2 = `${line2} ${word}`;
      line2 = next2;
    }
  }
  return [line1, line2 || null];
}

// Custom Y-axis tick for agent names: full label, wrap to max 2 lines, left of axis
const CustomYAxisTick = ({ x, y, payload }: any) => {
  const [line1, line2] = splitIntoTwoLines(payload.value ?? "");
  const lineHeight = 14;
  // In local coords the axis is at 0; put text to the left (negative x), end of text at -8
  const textEndX = -8;
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="end"
        fill="#666"
        fontSize={14}
        x={textEndX}
        y={0}
        dy={4}
      >
        <tspan x={textEndX} dy={0}>
          {line1}
        </tspan>
        {line2 != null && (
          <tspan x={textEndX} dy={lineHeight}>
            {line2}
          </tspan>
        )}
      </text>
    </g>
  );
};

export default function Performance(): JSX.Element {
  const user = useSelector((state: RootState) => state.auth.user);
  const { instance } = useMsal();
  const dispatch = useAppDispatch();

  // Get user preferences from Redux store
  const { userPreferences, userPreferencesLoading, userPreferencesError } =
    useSelector((state: RootState) => state.agents);

  const loading = userPreferencesLoading;
  const error = userPreferencesError;

  // Fetch user preferences on component mount
  useEffect(() => {
    dispatch(fetchUserPreferences());
  }, [dispatch]);

  // Extract thumbs up/down percentages from API data
  const thumbsUp = userPreferences?.total_thumbs_up_percentage ?? 0;
  const thumbsDown = userPreferences?.total_thumbs_down_percentage ?? 0;

  // Transform API data to chart format
  const agentData = useMemo(() => {
    if (!userPreferences?.All_agents_rating) return [];

    return userPreferences.All_agents_rating.map((agent) => ({
      name: agent.agent_name,
      thumbsUp: agent.thumbs_up_percentage,
      thumbsDown: agent.thumbs_down_percentage,
    }));
  }, [userPreferences]);

  const onClickLogout = async () => {
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: "/",
      });
      dispatch(clearUser());
      window.location.href = "/";
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  // Show full-page loader while data is being fetched
  if (loading) {
    return (
      <PageLoader label="Loading user feedback data..." activeNav="search" />
    );
  }

  return (
    <div className="dashboard-root">
      <Sidebar
        activeNav="search"
        user={user || undefined}
        onLogout={onClickLogout}
      />
      <main className="main-area">
        <header className="header">
          <div className="header-left">
            <h1 className="page-title">Performance: User Feedback </h1>
          </div>
        </header>

        <div className="scrollContent">
          <div className={styles.content}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {!error && (
              <>
                <section className={styles.userFeedbackSection}>
                  {/* <h2 className={styles.sectionTitle}>User Feedback</h2> */}
                  <div className={styles.feedbackOptions}>
                    <div className={styles.feedbackOption}>
                      <div className={styles.feedbackIcon}>👍</div>
                      <div className={styles.feedbackContent}>
                        <div className={styles.feedbackLabel}>Thumbs Up</div>
                        <div className={styles.feedbackPercentage}>
                          {thumbsUp}%
                        </div>
                      </div>
                    </div>
                    <div className={styles.feedbackOption}>
                      <div className={styles.feedbackIcon}>👎</div>
                      <div className={styles.feedbackContent}>
                        <div className={styles.feedbackLabel}>Thumbs Down</div>
                        <div className={styles.feedbackPercentage}>
                          {thumbsDown}%
                        </div>
                      </div>
                    </div>
                  </div>
                </section>

                <section
                  className={`${styles.chartSection} ${styles.userFeedbackChart}`}
                >
                  <h2 className={styles.sectionTitle}>
                    User Feedback by Agent
                  </h2>
                  <ResponsiveContainer width="100%" height={560}>
                    <BarChart
                      layout="vertical"
                      data={agentData}
                      margin={{ top: 20, right: 30, left: 250, bottom: 30 }}
                      barCategoryGap={24}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        type="number"
                        domain={[0, 100]}
                        label={{
                          value: "Percentage (%)",
                          position: "insideBottom",
                          offset: -8,
                        }}
                      />
                      <YAxis
                        type="category"
                        dataKey="name"
                        width={180}
                        tick={<CustomYAxisTick />}
                        interval={0}
                      />
                      <Tooltip />
                      <Legend />
                      <Bar
                        dataKey="thumbsUp"
                        stackId="a"
                        fill="#08ADC2"
                        name="% Thumbs Up"
                        barSize={24}
                      />
                      <Bar
                        dataKey="thumbsDown"
                        stackId="a"
                        fill="#ff3d52"
                        name="% Thumbs Down"
                        barSize={24}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </section>
              </>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
